import { useEffect } from "react";

type JsonLd = Record<string, unknown>;

// Baked in at build time from vite.config.ts so canonical/OG/JSON-LD URLs
// always point at the canonical production domain, even when the site is
// visited through an alias domain (e.g. *.replit.app).
declare const __SITE_ORIGIN__: string;

type SeoProps = {
  title: string;
  description: string;
  path?: string;
  noindex?: boolean;
  ogType?: "website" | "article";
  jsonLd?: JsonLd[];
};

export function publicSiteOrigin(): string {
  if (typeof __SITE_ORIGIN__ !== "undefined" && __SITE_ORIGIN__) {
    return __SITE_ORIGIN__;
  }
  return window.location.origin;
}

function siteOrigin(): string {
  return publicSiteOrigin();
}

function basePath(): string {
  return import.meta.env.BASE_URL.replace(/\/$/, "");
}

function absoluteUrl(path: string): string {
  return `${siteOrigin()}${basePath()}${path === "/" ? "/" : path}`;
}

function upsertMeta(
  selector: string,
  create: () => HTMLElement,
  content: string,
): () => void {
  let el = document.head.querySelector<HTMLElement>(selector);
  const created = !el;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", content);
  const target = el;
  return () => {
    if (created) target.remove();
    else if (prev !== null) target.setAttribute("content", prev);
  };
}

function upsertNamedMeta(name: string, content: string): () => void {
  return upsertMeta(
    `meta[name="${name}"]`,
    () => {
      const m = document.createElement("meta");
      m.setAttribute("name", name);
      return m;
    },
    content,
  );
}

function upsertPropertyMeta(property: string, content: string): () => void {
  return upsertMeta(
    `meta[property="${property}"]`,
    () => {
      const m = document.createElement("meta");
      m.setAttribute("property", property);
      return m;
    },
    content,
  );
}

function upsertCanonical(href: string): () => void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  const created = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  const target = el;
  return () => {
    if (created) target.remove();
    else if (prev !== null) target.setAttribute("href", prev);
  };
}

export function Seo({
  title,
  description,
  path = "/",
  noindex = false,
  ogType = "website",
  jsonLd,
}: SeoProps) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const url = absoluteUrl(path);
    const image = absoluteUrl("/opengraph.jpg");

    const restores: Array<() => void> = [
      upsertNamedMeta("description", description),
      upsertNamedMeta("robots", noindex ? "noindex, nofollow" : "index, follow"),
      upsertCanonical(url),
      upsertPropertyMeta("og:title", title),
      upsertPropertyMeta("og:description", description),
      upsertPropertyMeta("og:type", ogType),
      upsertPropertyMeta("og:url", url),
      upsertPropertyMeta("og:image", image),
      upsertPropertyMeta("og:site_name", "PalNote"),
      upsertNamedMeta("twitter:card", "summary_large_image"),
      upsertNamedMeta("twitter:title", title),
      upsertNamedMeta("twitter:description", description),
      upsertNamedMeta("twitter:image", image),
    ];

    const scripts: HTMLScriptElement[] = [];
    for (const data of jsonLd ?? []) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo", "true");
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
      scripts.push(s);
    }

    return () => {
      document.title = prevTitle;
      for (const restore of restores) restore();
      for (const s of scripts) s.remove();
    };
  }, [title, description, path, noindex, ogType, JSON.stringify(jsonLd)]);

  return null;
}

/**
 * Marks a non-marketing page (app pages, auth pages, 404) as noindex without
 * touching any other head tags.
 */
export function NoIndex() {
  useEffect(() => {
    const restore = upsertNamedMeta("robots", "noindex, nofollow");
    return restore;
  }, []);

  return null;
}
